import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { reach } from 'yup';

const useIsFieldRequired = (name) => {
  const { control, watch } = useFormContext();

  const resolver = control._options?.resolver;

  const dynamicRequired = useMemo(() => {
    if (!resolver?.getContext) return undefined;

    const context = resolver.getContext();

    return !!context?.requiredFields?.[name];
  }, [resolver, name]);

  const schema = resolver?.schema ?? resolver?.schemaFactory?.();

  const dependentFields = schema?.fields?.[name]?.deps ?? [];

  const depValues = watch(dependentFields);

  const allValues = watch();

  const [isRequired, setIsRequired] = useState(dynamicRequired ?? false);

  useEffect(() => {
    if (dynamicRequired !== undefined) {
      setIsRequired(dynamicRequired);
      return;
    }

    try {
      if (!schema) return;

      // schema.fields only has direct top-level keys, so a nested/array path (e.g. an
      // RHF field-array name like "conditions.0.field") never resolves here. Yup.reach walks
      // the schema for those paths — only attempted as a fallback so every existing top-level
      // field keeps resolving exactly as before.
      const fieldSchema =
        schema.fields?.[name] ??
        (name?.includes('.') ? reach(schema, name, allValues, allValues) : undefined);

      if (!fieldSchema) return;

      const resolvedField = fieldSchema.resolve({
        value: allValues[name],
        parent: allValues,
        context: allValues,
      });

      const description = resolvedField.describe();

      setIsRequired(
        description.optional === false || description.tests?.some((t) => t.name === 'required')
      );
    } catch {
      setIsRequired(false);
    }
  }, [dynamicRequired, name, schema, allValues, JSON.stringify(depValues)]);

  return isRequired;
};

export default useIsFieldRequired;
